import React from 'react';

import { useIntl } from 'react-intl';

import { MEMBERSHIP_CONFIGURATION_RESOURCE } from 'src/features/membership/api/useGetMembershipConfiguration';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';
import { LIST_MEMBERSHIP_TYPES_RESOURCE } from 'src/features/membershipType/api/useListMembershipTypes';
import { useUpdateMembershipType } from 'src/features/membershipType/api/useUpdateMembershipType';
import MembershipTypeWizardForm from 'src/features/membershipType/components/wizard/MembershipTypeWizardForm';
import { MembershipType } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';

interface UpdateMembershipTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
    selectedMembershipTemplate?: MembershipType;
}

function UpdateMembershipTypeFormModal<TData>({
    className,
    selectedMembershipTemplate,
    children,
}: UpdateMembershipTypeFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const templateName = selectedMembershipTemplate?.translations.find((temp) => temp.language === locale)?.name ?? '';
    const { mutate, isPending } = useUpdateMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_TYPES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_CONFIGURATION_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_CONFIGURATION_RESOURCE] });

            toast({
                title: formatMessage(membershipTypeMessages.updateTemplateSuccess),
                description: formatMessage(membershipTypeMessages.updateTemplateSuccessDescription, {
                    entry: templateName,
                }),
            });
        },
    });

    return (
        <MembershipTypeWizardForm
            selectedMembershipTemplate={selectedMembershipTemplate}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ parkingId, id: selectedMembershipTemplate?.id || '', ...values }, { onSuccess, onError })
            }
            title={formatMessage(membershipTypeMessages.editTemplate)}
            isPending={isPending}
        >
            {children}
        </MembershipTypeWizardForm>
    );
}

export default UpdateMembershipTypeFormModal;
